/**
 * El grabador: `getUserMedia` + `MediaRecorder`, con un timer y un tope.
 *
 * ── POR QUÉ UN HOOK Y NO CÓDIGO DENTRO DEL COMPONENTE ──────────────────────
 * Una grabación abre un recurso del sistema operativo: mientras el `MediaStream`
 * viva, el teléfono muestra el punto rojo y el navegador cuenta la pestaña como
 * "grabando". Si la persona toca "Siguiente" a mitad de una grabación, el
 * componente se desmonta y ese stream queda abierto para siempre. El cierre
 * tiene que ser un `useEffect` de limpieza, y para eso el estado tiene que estar
 * acá y no repartido en el render.
 *
 * ── EL PERMISO NEGADO NO ES UN ERROR ────────────────────────────────────────
 * Es una preferencia. Se reporta como `denied` para que la pantalla caiga al
 * teclado sin drama y sin volver a pedirlo: en iOS el segundo `getUserMedia`
 * después de un "No permitir" ni siquiera muestra el diálogo, así que insistir
 * sólo produce un botón que no hace nada.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_RECORDING_SECONDS,
  MIN_AUDIO_BYTES,
  isRecordingSupported,
  pickRecorderMimeType,
} from './voice';

export type RecorderStatus = 'idle' | 'starting' | 'recording';

/** `denied` = dijo que no. `failed` = el aparato no pudo. `empty` = no se oyó. */
export type RecorderProblem = 'denied' | 'unsupported' | 'failed' | 'empty' | null;

interface Options {
  /** Llega el audio y cuántos segundos duró, ya con el mínimo de peso pasado. */
  onCapture: (audio: Blob, seconds: number) => void;
}

export function useVoiceRecorder({ onCapture }: Options) {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [seconds, setSeconds] = useState(0);
  const [problem, setProblem] = useState<RecorderProblem>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discardRef = useRef(false);
  const aliveRef = useRef(true);

  // El callback se lee por referencia: el componente lo redefine en cada render
  // y no queremos reabrir el micrófono por eso. Se refresca en un efecto y no
  // durante el render, que es una escritura fuera de turno.
  const onCaptureRef = useRef(onCapture);
  useEffect(() => {
    onCaptureRef.current = onCapture;
  }, [onCapture]);

  const releaseStream = useCallback(() => {
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      releaseStream();
      setStatus('idle');
      return;
    }
    try {
      recorder.stop();
    } catch {
      releaseStream();
      setStatus('idle');
    }
  }, [releaseStream]);

  /** Cortar y tirar: el audio no se transcribe. */
  const cancel = useCallback(() => {
    discardRef.current = true;
    stop();
  }, [stop]);

  const start = useCallback(async () => {
    if (recorderRef.current) return;
    if (!isRecordingSupported()) {
      setProblem('unsupported');
      return;
    }

    setProblem(null);
    setSeconds(0);
    setStatus('starting');
    discardRef.current = false;
    chunksRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (!aliveRef.current) return;
      const name = err instanceof Error ? err.name : '';
      setProblem(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'failed');
      setStatus('idle');
      return;
    }

    // Se desmontó mientras el permiso estaba en pantalla: el stream ya está
    // abierto y hay que cerrarlo, o el punto rojo se queda prendido.
    if (!aliveRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    let recorder: MediaRecorder;
    try {
      const mimeType = pickRecorderMimeType();
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      // Un mimeType que `isTypeSupported` aprobó y el constructor rechaza sí
      // existe en WebViews viejos: se reintenta dejando elegir al navegador.
      try {
        recorder = new MediaRecorder(stream);
      } catch {
        stream.getTracks().forEach((track) => track.stop());
        setProblem('failed');
        setStatus('idle');
        return;
      }
    }

    streamRef.current = stream;
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      const type = recorder.mimeType || chunksRef.current[0]?.type || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      releaseStream();
      if (!aliveRef.current) return;
      setStatus('idle');
      setSeconds(0);
      if (discardRef.current) return;
      // Apretar y soltar: no hay audio que mandar y el servidor devolvería 422.
      if (blob.size < MIN_AUDIO_BYTES) {
        setProblem('empty');
        return;
      }
      onCaptureRef.current(blob, Math.min(elapsed, MAX_RECORDING_SECONDS));
    };

    recorder.onerror = () => {
      discardRef.current = true;
      releaseStream();
      if (!aliveRef.current) return;
      setProblem('failed');
      setStatus('idle');
    };

    try {
      recorder.start();
    } catch {
      releaseStream();
      setProblem('failed');
      setStatus('idle');
      return;
    }

    setStatus('recording');

    // El tope se cumple del lado del cliente y no esperando el 413: subir 90 s
    // por una red móvil para que rebote es gastar los datos de la persona.
    tickRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setSeconds(elapsed);
      if (elapsed >= MAX_RECORDING_SECONDS) stop();
    }, 250);
  }, [releaseStream, stop]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      discardRef.current = true;
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch {
          /* el stream se cierra igual abajo */
        }
      }
      releaseStream();
    };
  }, [releaseStream]);

  const clearProblem = useCallback(() => setProblem(null), []);

  return {
    status,
    seconds,
    problem,
    recording: status === 'recording',
    start,
    stop,
    cancel,
    clearProblem,
  };
}
